import classnames from 'classnames';
import * as React from 'react';

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice, DiscountLine, Cart } from '@msdyn365-commerce/retail-proxy';

export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    discountLine?: DiscountLine[] | undefined;
    cartData?: Cart;
    updatedPrice?: number;
    currencyCode?: string;
    getTierId?: () => number | undefined;
    clubPlusDiscountsOfferId?: DiscountLine | undefined | false;
}
export interface IPriceComponent extends IComponent<IPriceComponentProps> {}

const PriceComponentActions = {};

const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps) => {
    // CustomerContextualPrice could be 0
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    //loyalty &&

    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: IPriceComponentProps) => {
    const originalPrice = getOriginalPrice(props);

    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

const getOriginalPrice = (props: IPriceComponentProps) => {
    return Math.max(props.data.price.BasePrice || 0, props.data.price.TradeAgreementPrice || 0, props.data.price.AdjustedPrice || 0);
};

const renderCurrentPrice = (props: IPriceComponentProps, clubPlusDiscountsOfferId?: DiscountLine, checkUserLoyalty?: boolean): JSX.Element | undefined => {
    const initialPrice = formatCurrency(props, props.data.price.CustomerContextualPrice);
    return (
        <span className={`msc-price__actual ${clubPlusDiscountsOfferId && checkUserLoyalty ? "msc-price__strikethrough" : ""}`} itemProp='price'>
            {initialPrice}
        </span>
    );
};

const formatCurrency = (props: IPriceComponentProps, price: number | undefined): string | undefined => {
    if (price === undefined) {
        return undefined;
    }

    if (price === 0 && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price, props.currencyCode);
};

const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element | null => {
    const originalPrice = getOriginalPrice(props);
    const initialPrice = formatCurrency(props, originalPrice);

    const checkClubPlusDiscounts = props.discountLine && props.discountLine.find(x => x.OfferId === props.context.app.config.discountId);
    const checkUserLoyalty = props.getTierId && props.getTierId() === props.context.app.config.LoyaltyTierId ? true : undefined;
        // props.cartData &&
        // props.cartData.AffiliationLines &&
        // props.cartData.AffiliationLines.find(x => x.LoyaltyTierId === props.context.app.config.LoyaltyTierId);
    const checkNonClubPlusDiscounts = props.discountLine && props.discountLine.find(x => x.OfferId !== props.context.app.config.discountId);

    const clubPlusFormatCurrency = (
        props: IPriceComponentProps,
        price: number | undefined,
        nonClubPlusPrice: number | undefined
    ): string | undefined => {
        if (price === undefined) {
            return undefined;
        }
        if (price === 0 && props.freePriceText) {
            return props.freePriceText;
        }

        if (nonClubPlusPrice === undefined) {
            return undefined;
        }
        if (nonClubPlusPrice === 0 && props.freePriceText) {
            return props.freePriceText;
        }

        return props.context.cultureFormatter.formatCurrency(nonClubPlusPrice, props.context.request.channel?.Currency);
    };

    const renderClubPlusCurrentPrice = (props: IPriceComponentProps): JSX.Element | undefined => {
        const initialPrice = clubPlusFormatCurrency(props, props.data.price.CustomerContextualPrice, props.updatedPrice);
        return (
            <span className='msc-price__actual' itemProp='price'>
                {initialPrice}
            </span>
        );
    };

    return (
        <>
            {checkClubPlusDiscounts && checkUserLoyalty ? (
                <>
                    <span className='sr-only'>
                        {' '}
                        {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
                    </span>
                    {/* VSI Customization - Start - 23/10/2020 */}
                    {/* Displayed discounted price on the left, Actual price on the right */}

                    <span className='msc-price__strikethrough' aria-hidden='true'>
                        {initialPrice}
                    </span>
                </>
            ) : checkClubPlusDiscounts && checkNonClubPlusDiscounts && checkUserLoyalty === undefined ? (
                <>
                    <span className='sr-only'>
                        {' '}
                        {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
                    </span>
                    {/* VSI Customization - Start - 23/10/2020 */}
                    {/* Displayed discounted price on the left, Actual price on the right */}
                    <span className='msc-price__strikethrough' aria-hidden='true'>
                        {initialPrice}
                    </span>
                    <span className='msc-price__salePrice' aria-hidden='true'>
                        {renderClubPlusCurrentPrice(props)}
                    </span>
                    {/* VSI - Customization - End */}
                    {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
                </>
            ) : checkClubPlusDiscounts && checkUserLoyalty === undefined ? (
                <>
                    <span className='sr-only'>
                        {' '}
                        {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
                    </span>
                    {/* VSI Customization - Start - 23/10/2020 */}
                    {/* Displayed discounted price on the left, Actual price on the right */}

                    <span className='msc-price__actual' aria-hidden='true'>
                        {initialPrice}
                    </span>
                </>
            ) : (
                <>
                    <span className='sr-only'>
                        {' '}
                        {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
                    </span>
                    {/* VSI Customization - Start - 23/10/2020 */}
                    {/* Displayed discounted price on the left, Actual price on the right */}
                    <span className='msc-price__strikethrough' aria-hidden='true'>
                        {initialPrice}
                    </span>
                    {props.clubPlusDiscountsOfferId && checkUserLoyalty ? (
                        <span className='msc-price__salePrice' aria-hidden='true'>
                            {renderCurrentPrice(props, props.clubPlusDiscountsOfferId, checkUserLoyalty)}
                        </span>
                    ) : (
                        <span className='msc-price__salePrice' aria-hidden='true'>
                            {renderCurrentPrice(props)}
                        </span>
                    )}
                    {/* VSI - Customization - End */}
                    {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
                </>
            )}
        </>
    );
};

// @ts-ignore
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>(
    'Price',
    { component: Price, ...PriceComponentActions }
);

export default PriceComponent;
